import {FormInstance} from 'antd/es/form';
import {useForm} from 'antd/es/form/Form';
import React, {
    forwardRef, useImperativeHandle, useState, Key,
} from 'react';

import {CustomTabs} from 'components/custom-tabs';
import {
    AdditionalFields,
} from 'pages/ais/documents-register-page/modals/document-card-modal/tabs-components/additional-fields/additional-fields';
import {TStateSetter} from 'shared/types/generics';
import {IAisRegisterDocumentDto} from 'store/slices/ais-slice/ais-slice-types';

import {EFormMode} from '../../../../../components/form';
import {usePudUpload} from '../../hooks/use-pud-upload';
import {EPudUploadModalMode} from '../../hooks/use-pud-upload/use-pud-upload';
import {EDocumentCardModalTabKey} from './document-card-modal-constants';
import {ChangesJournal} from './tabs-components/changes-journal';
import {MainInfo} from './tabs-components/main-info';
import {TaxInfo} from './tabs-components/tax-info';

interface IDocumentCardModalContentProps {
    initialRegisterDocument?: IAisRegisterDocumentDto;
    selectedTabKey: EDocumentCardModalTabKey;
    setSelectedTabKey: TStateSetter<EDocumentCardModalTabKey>;
    formMode: EFormMode;
    isDisabled?: boolean;
}

export interface IDocumentCardModalContentRef {
    mainInfoTabForm: FormInstance;
    includedTaxesForm: FormInstance;
    pudUploadForm: FormInstance;
    additionalFieldsForm: FormInstance;

    handlePudFormFinish: (values: any) => Promise<any>;

    // registerDocument НЕ выносить в стейт компонента выше, данные не будут сбрасываться
    // при выходе из модалки
    registerDocument: IAisRegisterDocumentDto | undefined;
    setRegisterDocument: TStateSetter<IAisRegisterDocumentDto | undefined>;
}

export const DocumentCardModalContent = forwardRef<
    IDocumentCardModalContentRef,
    IDocumentCardModalContentProps>((props: IDocumentCardModalContentProps, ref) => {
        const {
            initialRegisterDocument,
            selectedTabKey,
            setSelectedTabKey,
            formMode,
            isDisabled,
        } = props;

        const [mainInfoTabForm] = useForm();
        const [additionalFieldsForm] = useForm();
        const [includedTaxesForm] = useForm();

        const [registerDocument, setRegisterDocument] = useState<IAisRegisterDocumentDto | undefined>(
            initialRegisterDocument,
        );

        const {
            PUDUploadFormJSX,
            form: pudUploadForm,
            handleFormFinish: handlePudFormFinish,
        } = usePudUpload({
            registerDocument,
            className: 'mt-1',
            mode: EPudUploadModalMode.inDocumentCard,
            isDisabled,
        });

        useImperativeHandle(ref, () => ({
            mainInfoTabForm,
            includedTaxesForm,
            pudUploadForm,
            additionalFieldsForm,
            registerDocument,
            setRegisterDocument,
            handlePudFormFinish,
        }));

        return (
            <CustomTabs
                variant="up-menu"
                selectedTabKey={selectedTabKey}
                setSelectedTabKey={setSelectedTabKey as TStateSetter<Key>}
                defaultRenderBehaviourOnSwitchingTabs="preserve"
                tabs={[{
                    title: 'Основная информация',
                    content: (
                        <MainInfo
                            registerDocument={registerDocument}
                            form={mainInfoTabForm}
                            formMode={formMode}
                            pudUploadJSX={PUDUploadFormJSX}
                            isDisabled={isDisabled}
                        />
                    ),
                    key: EDocumentCardModalTabKey.main,
                },
                {
                    title: 'Дополнительные поля',
                    content: (
                        <AdditionalFields
                            id={registerDocument?.id}
                            form={additionalFieldsForm}
                            isDisabled={isDisabled}
                        />
                    ),
                    key: EDocumentCardModalTabKey.additional,
                    isTabTitleHidden: formMode === EFormMode.CREATE,
                    noRenderContent: formMode === EFormMode.CREATE,
                },
                {
                    title: 'Налоги',
                    content: (
                        <TaxInfo
                            form={includedTaxesForm}
                            documentMappingId={registerDocument?.id}
                            isDisabled={isDisabled}
                        /> // todo
                    ),
                    key: EDocumentCardModalTabKey.tax,
                    // если делать условный рендеринг через модифицирование tabs, то появляются визуальные баги
                    // поэтому скрываем отдельно наименование и содержимое
                    isTabTitleHidden: formMode === EFormMode.CREATE,
                    noRenderContent: formMode === EFormMode.CREATE,
                }, {
                    title: 'Журнал изменений',
                    key: EDocumentCardModalTabKey.changesJournal,
                    content: (
                        <ChangesJournal /> // todo
                    ),
                    isTabTitleHidden: formMode === EFormMode.CREATE,
                    noRenderContent: formMode === EFormMode.CREATE,
                },
                ]}
            />
        );
    });
